<template>
	<div v-if="isDataLoading" class="text-center py-8">
		Loading data, please wait...
	</div>
	<div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 mb-8 mt-12" style="align-items: center;">
		<!-- Image Section -->
		<div class="px-1 md:px-12 image-container">
			<div class="img-section mx-auto rounded-lg"
				:style="{ backgroundImage: `url('${urlLocation}${ppcData.marketing_block_7_image}')` }"> &nbsp;
			</div>
		</div>

		<!-- Content Section -->
		<div class="py-2 px-6">
			<h2 class="pb-2 font-semibold">
				{{ ppcData.marketing_block_7_heading }}
			</h2>

			<!-- Features List -->
			<div class="py-2 flex flex-col">

				<div v-for="(feature, index) in programsAndServices" :key="index" class="py-2 feature-item">
					<svg viewBox="0 0 24 24" class="icon mx-1 check-icon" style="min-width:25px;">
						<path fill-rule="evenodd"
							d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
							clip-rule="evenodd" />
					</svg>

					<span class="mb-1">
						<span class="feature-desc" v-html="feature.ServiceDesc"></span>

						<div class="relative inline-block">

							<button class="text-white rounded" @mouseenter="currentIndex = index"
								@mouseleave="currentIndex = null">
								<svg viewBox="0 0 24 24" class="icon info-icon" style="margin-bottom: -5px;">
									<path fill-rule="evenodd"
										d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 0 1-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 0 1-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 0 1-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584ZM12 18a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
										clip-rule="evenodd" />
								</svg>
							</button>

							<!-- Tooltip -->
							<div v-show="currentIndex === index"
								class="absolute bg-gray-700 text-white text-xs px-2 py-1 bottom-full mb-2 left-1/2 transform -translate-x-1/2 rounded-md"
								style="max-width: 500px;min-width: 250px;">
								<span v-html="serviceDefId(feature.fk_ServiceDefId)"></span>
							</div>

						</div>

					</span>
				</div>

			</div>

			<!-- Footer Copy -->
			<p class="text-sm px-2 py-2 text-base pt-10">
				{{ ppcData.marketing_block_7_copy }}
			</p>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useStore } from "vuex";
import type { Store } from "vuex/types/index.js";
import type UPPackageProgramsAndServicesState from "~/domains/store/branchinfo/UPPackageProgramsAndServicesState";
import type State from "~/domains/store/State";

const store: Store<State> = useStore();

const props = defineProps({
	ppcData: {
		type: Object,
		required: true,
	},
	urlLocation: {
		type: String,
		required: true,
	},
	currentPackage: {
		type: [Number, Object],
		default: null,
	},
});

const programsAndServices = ref<UPPackageProgramsAndServicesState[]>([]);
const isDataLoading = ref(true);
const currentIndex = ref<number | null>(null);

watch(
	() => store.state.persistedState.branchInfo,
	(newValue) => {
		if (newValue) {
			loadProgramsAndServices();
		}
	},
	{ immediate: false, deep: true }
);

const loadProgramsAndServices = async () => {
	const persistedState = store.state.persistedState;
	if (!persistedState) return;

	const upPackage = persistedState?.branchInfo?.UPPackage;
	const IMCYear = persistedState?.branchInfo?.IMCYear;

	console.log("UPPackage data:", upPackage);
	console.log("IMCYear:", IMCYear);
	console.log("Current Package:", props?.currentPackage);

	if (upPackage) {
		const filteredData = upPackage.find(
			(item) => item.Year === IMCYear && props?.currentPackage == item.PNameID
		);

		if (filteredData) {
			programsAndServices.value = filteredData["Programs and Services"];
		}
	}

	isDataLoading.value = false;
};

const stripHtmlTags = (htmlString: string): string => {
	const tempDiv = document.createElement("div");
	tempDiv.innerHTML = htmlString;
	return tempDiv.textContent || tempDiv.innerText || "";
};

const serviceDefId = (serviceDefId: number): string => {
	const serviceData = store.state.persistedState?.serviceData;

	const htmlString = serviceData?.services?.[serviceDefId]?.ServiceDesc || "";
	return stripHtmlTags(htmlString);
};

onMounted(() => {
	loadProgramsAndServices();
});
</script>

<style scoped>
.image-container {
	height: 350px;
	width: 100%;
}

.img-section {
	background-repeat: no-repeat;
	background-position: center center;
	background-size: cover;
	height: 100%;
}

.icon {
	width: 25px;
	height: 25px;
}

.check-icon {
	fill: #00810e;
}

.info-icon {
	fill: #517185;
}

.feature-item {
	font-size: 1rem;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.feature-title {
	font-weight: bold;
}

.feature-desc {
	font-size: 0.9rem;
	color: #666;
}

.rounded-lg {
	border-radius: 20px;
}

.text-base {
	font-size: 1rem;
}
</style>
